import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COUNTRY_CODES } from "@/lib/constants";
import { Loader2 } from "lucide-react";

export default function OnboardingModal({ open, onOpenChange, onComplete }) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    cc: "+1",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  const update = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: "" }));
    setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.company.trim()) e.company = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    else if (!/^\d{6,15}$/.test(form.phone.replace(/[\s-]/g, "")))
      e.phone = "Invalid phone number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setBusy(true);
    setServerError("");
    try {
      const payload = {
        name: form.name,
        company: form.company,
        phone: `${form.cc} ${form.phone}`,
      };
      await onComplete(payload);
    } catch (err) {
      setServerError(err.message || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  const inputCls = (hasErr) =>
    `mt-1.5 w-full px-3 py-2.5 rounded-sm text-sm outline-none transition-colors font-sans`;

  const inputStyle = (hasErr) => ({
    background: "var(--sdb-bg)",
    border: `1px solid ${hasErr ? "var(--sdb-error)" : "var(--sdb-border)"}`,
    color: "var(--sdb-text)",
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="onboarding-modal"
        className="backdrop-blur-xl rounded-sm max-w-[440px] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        style={{
          background: "var(--sdb-glass)",
          border: "1px solid var(--sdb-border)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.4)",
        }}
      >
        <DialogHeader>
          <div
            className="font-mono text-[10px] tracking-[3px] uppercase mb-1"
            style={{ color: "var(--sdb-accent)" }}
          >
            WELCOME
          </div>
          <DialogTitle className="text-xl font-semibold" style={{ color: "var(--sdb-text)" }}>
            Complete Your Profile
          </DialogTitle>
          <DialogDescription style={{ color: "var(--sdb-text-secondary)" }} className="text-sm">
            Tell us about yourself so we can personalize your experience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Name */}
          <div>
            <Label
              className="font-mono text-[11px] uppercase tracking-wider"
              style={{ color: "var(--sdb-text-muted)" }}
            >
              Full Name
            </Label>
            <input
              data-testid="onboarding-name"
              type="text"
              placeholder="Jane Smith"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className={inputCls(errors.name)}
              style={inputStyle(errors.name)}
            />
            {errors.name && (
              <p className="text-[10px] mt-1 font-mono" style={{ color: "var(--sdb-error)" }}>
                {errors.name}
              </p>
            )}
          </div>

          {/* Company */}
          <div>
            <Label
              className="font-mono text-[11px] uppercase tracking-wider"
              style={{ color: "var(--sdb-text-muted)" }}
            >
              Company
            </Label>
            <input
              data-testid="onboarding-company"
              type="text"
              placeholder="Acme AI"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              className={inputCls(errors.company)}
              style={inputStyle(errors.company)}
            />
            {errors.company && (
              <p className="text-[10px] mt-1 font-mono" style={{ color: "var(--sdb-error)" }}>
                {errors.company}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label
              className="font-mono text-[11px] uppercase tracking-wider"
              style={{ color: "var(--sdb-text-muted)" }}
            >
              Phone Number
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={form.cc} onValueChange={(v) => update("cc", v)}>
                <SelectTrigger
                  data-testid="onboarding-country-code"
                  className="w-[110px] text-xs font-mono rounded-sm h-auto py-2.5 focus:ring-0"
                  style={{
                    background: "var(--sdb-bg)",
                    border: "1px solid var(--sdb-border)",
                    color: "var(--sdb-text)",
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  className="rounded-sm"
                  style={{ background: "var(--sdb-surface)", border: "1px solid var(--sdb-border)" }}
                >
                  {COUNTRY_CODES.map((c) => (
                    <SelectItem
                      key={c.code}
                      value={c.code}
                      className="font-mono text-xs"
                      style={{ color: "var(--sdb-text-secondary)" }}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                data-testid="onboarding-phone"
                type="tel"
                placeholder="50 123 4567"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className={`flex-1 ${inputCls(errors.phone)}`}
                style={inputStyle(errors.phone)}
              />
            </div>
            {errors.phone && (
              <p className="text-[10px] mt-1 font-mono" style={{ color: "var(--sdb-error)" }}>
                {errors.phone}
              </p>
            )}
          </div>

          {serverError && (
            <p
              data-testid="onboarding-server-error"
              className="text-[11px] font-mono"
              style={{ color: "var(--sdb-error)" }}
            >
              {serverError}
            </p>
          )}

          <Button
            data-testid="onboarding-submit-btn"
            onClick={handleSubmit}
            disabled={busy}
            className="w-full gradient-bg text-black font-semibold text-sm rounded-sm py-3 h-auto glow-btn hover:text-black"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Get Started"
            )}
          </Button>

          <p className="text-center font-mono text-[10px]" style={{ color: "var(--sdb-text-subtle)" }}>
            This helps us tailor MockDB to your needs.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
